import { SkeletonTable } from '../../components/LoadingSkeleton/LoadingSkeleton';
import { splitPhone } from '../../utils/phone';
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  fetchDealers,
  deleteDealerAsync,
  editDealerAsync,
  addDealerAsync,
  addOfficeAsync,
  clearMessages,
  selectSuccessMessage,
  selectDealersError,
  selectRefreshData,
  setRefreshData
} from '../../features/dealers/dealerSlice';
import './dealers.css';
import TrashIcon from '../../components/icons/TrashIcon';
import { Eye, EyeOff } from 'lucide-react';

const digitsOnly = (value, maxLength) => value.replace(/\D/g, '').slice(0, maxLength);
const cityOnly = (value) => value.replace(/[^a-zA-Z\s.'-]/g, '');
const preventNonNumericInput = (event) => {
  if (event.ctrlKey || event.metaKey || ['Backspace', 'Delete', 'Tab', 'ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
  if (!/^\d$/.test(event.key)) event.preventDefault();
};
const getDealerPassword = (dealer) => dealer?.dealerPassword || dealer?.dealerpassword || dealer?.password || dealer?.userPassword || '';
const formatOfficeAddress = (office) => {
  const address = office?.officeaddress || office?.officeAddress || office?.address || '';
  if (!address.trim()) return '';
  const location = [office?.city, office?.state].filter(Boolean).join(', ');
  return [address, location].filter(Boolean).join(', ') + (office?.zipcode ? ` - ${office.zipcode}` : '');
};

const Dealers = () => {
  const dispatch = useDispatch();
  const { dealers, loading } = useSelector((state) => state.dealers);
  const successMessage = useSelector(selectSuccessMessage);
  const errorMessage = useSelector(selectDealersError);
  const refreshData = useSelector(selectRefreshData);

  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [goToPage, setGoToPage] = useState('');
  const [openDropdown, setOpenDropdown] = useState(null);

  // Modal states
  const [isAddDealerModalOpen, setIsAddDealerModalOpen] = useState(false);
  const [isAddOfficeModalOpen, setIsAddOfficeModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [selectedDealer, setSelectedDealer] = useState(null);
  const [isOfficeSameAsDealer, setIsOfficeSameAsDealer] = useState(false);
  const [showDealerPassword, setShowDealerPassword] = useState(false);
  const [showOfficePassword, setShowOfficePassword] = useState(false);
  const [showAlert, setShowAlert] = useState(false);
  const [alertType, setAlertType] = useState('');
  const [alertMessage, setAlertMessage] = useState('');

  // US States list
  const usStates = [
    "AL", "AK", "AZ", "AR", "CA", "CO", "CT", "DE", "FL", "GA",
    "HI", "ID", "IL", "IN", "IA", "KS", "KY", "LA", "ME", "MD",
    "MA", "MI", "MN", "MS", "MO", "MT", "NE", "NV", "NH", "NJ",
    "NM", "NY", "NC", "ND", "OH", "OK", "OR", "PA", "RI", "SC",
    "SD", "TN", "TX", "UT", "VT", "VA", "WA", "WV", "WI", "WY", "INDIA"
  ];

  // Dealer Form Data
  const [dealerFormData, setDealerFormData] = useState({
    dealerName: '',
    dealerEmail: '',
    dealerMobile1: '',
    dealerMobile2: '',
    dealerPhoneCode1: '+1',
    dealerPhoneCode2: '+1',
    dealerPassword: '',
    address: '',
    city: '',
    state: '',
    zipcode: '',
    accesslevel: 'dealer'
  });

  // Office Form Data
  const [officeFormData, setOfficeFormData] = useState({
    officeName: '',
    officeMobile1: '',
    officeMobile2: '',
    officePhoneCode1: '+1',
    officePhoneCode2: '+1',
    officeemail: '',
    officepassword: '',
    officeaddress: '',
    city: '',
    state: '',
    zipcode: '',
    isbar: '0',
    officelicense: '',
    dealerId: ''
  });

  useEffect(() => {
    if (!isOfficeSameAsDealer) return;
    setOfficeFormData(prev => ({
      ...prev,
      officeName: dealerFormData.dealerName,
      officeMobile1: dealerFormData.dealerMobile1,
      officeMobile2: dealerFormData.dealerMobile2,
      officePhoneCode1: dealerFormData.dealerPhoneCode1,
      officePhoneCode2: dealerFormData.dealerPhoneCode2,
      officeemail: dealerFormData.dealerEmail,
      officeaddress: dealerFormData.address,
      city: dealerFormData.city,
      state: dealerFormData.state,
      zipcode: dealerFormData.zipcode
    }));
  }, [isOfficeSameAsDealer, dealerFormData]);

  const loadDealers = useCallback(() => dispatch(fetchDealers()), [dispatch]);

  // Initial load
  useEffect(() => {
    loadDealers();
  }, [loadDealers]);

  useEffect(() => {
    if (!refreshData) return;
    loadDealers();
    dispatch(setRefreshData(false));
  }, [refreshData, dispatch, loadDealers]);

  // Show success/error messages and refresh
  useEffect(() => {
    if (successMessage) {
      setAlertMessage(successMessage);
      setAlertType('success');
      setShowAlert(true);

      setTimeout(() => {
        setShowAlert(false);
        dispatch(clearMessages());
      }, 3000);
    }
    if (errorMessage) {
      setAlertMessage(errorMessage);
      setAlertType('error');
      setShowAlert(true);
      setTimeout(() => {
        setShowAlert(false);
        dispatch(clearMessages());
      }, 3000);
    }
  }, [successMessage, errorMessage, dispatch]);

  const getInitials = (name) => {
    if (!name) return '??';
    const words = name.trim().split(/\s+/);
    return `${words[0]?.[0] || ''}${words.length > 1 ? words[words.length - 1][0] : ''}`.toUpperCase();
  };

  const avatarColors = [
    '#2b6e9e', '#7c3aed', '#f59e0b', '#ef4444',
    '#10b981', '#f97316', '#8b5cf6', '#00b894'
  ];

  const getAvatarColor = (name) => {
    if (!name) return '#2b6e9e';
    return avatarColors[name.charCodeAt(0) % avatarColors.length];
  };

  // Filter dealers based on search term
  const filteredDealers = useMemo(() => {
    return dealers.filter(dealer =>
      [dealer.dealerName, dealer.dealerEmail, dealer.dealerId, dealer.dealerMobile1]
        .some(field => field && field.toLowerCase().includes(searchTerm.toLowerCase()))
    );
  }, [dealers, searchTerm]);

  // Reset to page 1 when search or rows per page changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, rowsPerPage]);

  // Calculate pagination values
  const totalItems = filteredDealers.length;
  const totalPages = Math.max(1, Math.ceil(totalItems / rowsPerPage));

  // Ensure current page is valid
  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages]);

  // Get current page items
  const getCurrentPageItems = useCallback(() => {
    const startIndex = (currentPage - 1) * rowsPerPage;
    const endIndex = Math.min(startIndex + rowsPerPage, totalItems);
    return filteredDealers.slice(startIndex, endIndex);
  }, [currentPage, rowsPerPage, filteredDealers, totalItems]);

  const currentItems = getCurrentPageItems();

  // Pagination handlers
  const handlePageChange = useCallback((page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
      setGoToPage('');
      // Scroll to top of table
      const tableElement = document.querySelector('.table-scroll');
      if (tableElement) {
        tableElement.scrollTop = 0;
      }
    }
  }, [totalPages]);

  const handleRowsPerPageChange = useCallback((e) => {
    const newRowsPerPage = Number(e.target.value);
    setRowsPerPage(newRowsPerPage);
    setCurrentPage(1);
    setGoToPage('');
  }, []);

  const handleGoToPage = useCallback((e) => {
    e.preventDefault();
    const pageNum = parseInt(goToPage);
    if (!isNaN(pageNum) && pageNum >= 1 && pageNum <= totalPages) {
      setCurrentPage(pageNum);
      setGoToPage('');
    }
  }, [goToPage, totalPages]);

  // Generate page numbers for display - FIXED
  const getPageNumbers = useCallback(() => {
    const pages = [];
    const maxVisible = 5;
    const halfVisible = Math.floor(maxVisible / 2);

    if (totalPages <= maxVisible) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      let startPage = Math.max(1, currentPage - halfVisible);
      let endPage = Math.min(totalPages, currentPage + halfVisible);

      // Adjust if we're at the beginning
      if (currentPage - halfVisible < 1) {
        endPage = Math.min(totalPages, endPage + (halfVisible - currentPage + 1));
      }
      
      // Adjust if we're at the end
      if (currentPage + halfVisible > totalPages) {
        startPage = Math.max(1, startPage - (currentPage + halfVisible - totalPages));
      }

      // Add first page and ellipsis if needed
      if (startPage > 1) {
        pages.push(1);
        if (startPage > 2) {
          pages.push('...');
        }
      }

      // Add middle pages
      for (let i = startPage; i <= endPage; i++) {
        pages.push(i);
      }

      // Add last page and ellipsis if needed
      if (endPage < totalPages) {
        if (endPage < totalPages - 1) {
          pages.push('...');
        }
        pages.push(totalPages);
      }
    }

    return pages;
  }, [currentPage, totalPages]);

  // Calculate displayed range
  const startItem = totalItems === 0 ? 0 : (currentPage - 1) * rowsPerPage + 1;
  const endItem = Math.min(currentPage * rowsPerPage, totalItems);

  const toggleDropdown = (index) => {
    setOpenDropdown(openDropdown === index ? null : index);
  };

  const handleDeleteClick = (dealer) => {
    setSelectedDealer(dealer);
    setIsDeleteModalOpen(true);
  };

  const handleConfirmDelete = async () => {
    const dealerToDelete = selectedDealer;
    setIsDeleteModalOpen(false);
    setSelectedDealer(null);

    if (dealerToDelete) {
      await dispatch(deleteDealerAsync(dealerToDelete.dealerId));
      // Refresh will happen via the successMessage useEffect
    }
  };

  const handleEditClick = (dealer) => {
    setSelectedDealer(dealer);
    setDealerFormData({
      dealerName: dealer.dealerName || '',
      dealerEmail: dealer.dealerEmail || '',
      dealerMobile1: splitPhone(dealer.dealerMobile1).number,
      dealerPhoneCode1: splitPhone(dealer.dealerMobile1).code,
      dealerMobile2: splitPhone(dealer.dealerMobile2).number,
      dealerPhoneCode2: splitPhone(dealer.dealerMobile2).code,
      dealerPassword: getDealerPassword(dealer),
      address: dealer.address || '',
      city: dealer.city || '',
      state: dealer.state || '',
      zipcode: dealer.zipcode || '',
      accesslevel: dealer.accesslevel || 'dealer'
    });
    setShowDealerPassword(false);
    setIsEditModalOpen(true);
  };

  const handleAddDealerClick = () => {
    setDealerFormData({
      dealerName: '',
      dealerEmail: '',
      dealerMobile1: '',
      dealerMobile2: '',
      dealerPhoneCode1: '+1', dealerPhoneCode2: '+1',
      dealerPassword: '',
      address: '',
      city: '',
      state: '',
      zipcode: '',
      accesslevel: 'dealer'
    });
    setOfficeFormData({
      officeName: '',
      officeMobile1: '',
      officeMobile2: '',
      officePhoneCode1: '+1', officePhoneCode2: '+1',
      officeemail: '',
      officepassword: '',
      officeaddress: '',
      city: '',
      state: '',
      zipcode: '',
      isbar: '0',
      officelicense: '',
      dealerId: ''
    });
    setIsOfficeSameAsDealer(false);
    setShowDealerPassword(false);
    setShowOfficePassword(false);
    setIsAddDealerModalOpen(true);
  };

  const handleAddOfficeClick = (dealer) => {
    setSelectedDealer(dealer);
    setOfficeFormData({
      officeName: '',
      officeMobile1: '',
      officeMobile2: '',
      officePhoneCode1: '+1',
      officePhoneCode2: '+1',
      officeemail: '',
      officepassword: '',
      officeaddress: '',
      city: '',
      state: '',
      zipcode: '',
      isbar: '0',
      officelicense: '',
      dealerId: dealer.dealerId
    });
    setShowOfficePassword(false);
    setIsAddOfficeModalOpen(true);
  };

  const resetDealerForm = () => {
    setDealerFormData({
      dealerName: '',
      dealerEmail: '',
      dealerMobile1: '',
      dealerMobile2: '',
      dealerPhoneCode1: '+1', dealerPhoneCode2: '+1',
      dealerPassword: '',
      address: '',
      city: '',
      state: '',
      zipcode: '',
      accesslevel: 'dealer'
    });
    setIsOfficeSameAsDealer(false);
  };

  const resetOfficeForm = () => {
    setOfficeFormData({
      officeName: '',
      officeMobile1: '',
      officeMobile2: '',
      officePhoneCode1: '+1', officePhoneCode2: '+1',
      officeemail: '',
      officepassword: '',
      officeaddress: '',
      city: '',
      state: '',
      zipcode: '',
      isbar: '0',
      officelicense: '',
      dealerId: ''
    });
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();

    const dealerData = {
      dealerName: dealerFormData.dealerName,
      dealerEmail: dealerFormData.dealerEmail,
      dealerMobile1: dealerFormData.dealerMobile1 ? dealerFormData.dealerPhoneCode1 + dealerFormData.dealerMobile1 : '',
      dealerMobile2: dealerFormData.dealerMobile2 ? dealerFormData.dealerPhoneCode2 + dealerFormData.dealerMobile2 : '',
      address: dealerFormData.address || '',
      city: dealerFormData.city || '',
      state: dealerFormData.state || '',
      zipcode: dealerFormData.zipcode || '',
      accesslevel: dealerFormData.accesslevel || 'dealer'
    };

    if (dealerFormData.dealerPassword) {
      dealerData.dealerPassword = dealerFormData.dealerPassword;
    }

    await dispatch(editDealerAsync({
      dealerId: selectedDealer.dealerId,
      dealerData: dealerData
    })).unwrap();

    await loadDealers();
    setIsEditModalOpen(false);
    setSelectedDealer(null);
    resetDealerForm();
  };

  const handleAddDealerSubmit = async (e) => {
    e.preventDefault();

    const dealerData = {
      dealerName: dealerFormData.dealerName,
      dealerEmail: dealerFormData.dealerEmail,
      dealerMobile1: dealerFormData.dealerMobile1 ? dealerFormData.dealerPhoneCode1 + dealerFormData.dealerMobile1 : '',
      dealerMobile2: dealerFormData.dealerMobile2 ? dealerFormData.dealerPhoneCode2 + dealerFormData.dealerMobile2 : '',
      dealerPassword: dealerFormData.dealerPassword,
      address: dealerFormData.address || '',
      city: dealerFormData.city || '',
      state: dealerFormData.state || '',
      zipcode: dealerFormData.zipcode || '',
      accesslevel: 'dealer',
      includeOffice: isOfficeSameAsDealer,
      officeName: officeFormData.officeName,
      officeMobile1: officeFormData.officeMobile1 ? officeFormData.officePhoneCode1 + officeFormData.officeMobile1 : '',
      officeMobile2: officeFormData.officeMobile2 ? officeFormData.officePhoneCode2 + officeFormData.officeMobile2 : '',
      officeemail: officeFormData.officeemail,
      officepassword: officeFormData.officepassword,
      officeaddress: officeFormData.officeaddress,
      officeCity: officeFormData.city,
      officeState: officeFormData.state,
      officeZipcode: officeFormData.zipcode,
      isbar: officeFormData.isbar,
      officelicense: officeFormData.officelicense
    };

    setIsAddDealerModalOpen(false);
    resetDealerForm();

    await dispatch(addDealerAsync(dealerData));
    // Refresh will happen via the successMessage useEffect
  };

  const handleAddOfficeSubmit = async (e) => {
    e.preventDefault();

    const officeData = {
      officeName: officeFormData.officeName,
      officeMobile1: officeFormData.officeMobile1 ? officeFormData.officePhoneCode1 + officeFormData.officeMobile1 : '',
      officeMobile2: officeFormData.officeMobile2 ? officeFormData.officePhoneCode2 + officeFormData.officeMobile2 : '',
      officeemail: officeFormData.officeemail,
      officepassword: officeFormData.officepassword,
      officeaddress: officeFormData.officeaddress,
      city: officeFormData.city,
      state: officeFormData.state,
      zipcode: officeFormData.zipcode,
      isbar: officeFormData.isbar,
      officelicense: officeFormData.officelicense || '',
      dealerId: selectedDealer.dealerId
    };

    setIsAddOfficeModalOpen(false);
    resetOfficeForm();

    await dispatch(addOfficeAsync(officeData));
    // Refresh will happen via the successMessage useEffect
  };

  const ExpandedRow = ({ dealer, isOpen }) => {
    if (!isOpen) return null;

    return (
      <tr className="expanded-row">
        <td colSpan="7">
          <div className="expanded-content">
            <div className="expanded-section">
              <h4>Dealer Information</h4>
              <div className="expanded-grid">
                <div className="expanded-item">
                  <span className="expanded-label">Dealer ID:</span>
                  <span className="expanded-value">{dealer.dealerId}</span>
                </div>
                <div className="expanded-item">
                  <span className="expanded-label">Dealer Name:</span>
                  <span className="expanded-value">{dealer.dealerName}</span>
                </div>
                <div className="expanded-item">
                  <span className="expanded-label">Email:</span>
                  <span className="expanded-value">{dealer.dealerEmail}</span>
                </div>
                <div className="expanded-item">
                  <span className="expanded-label">Mobile 1:</span>
                  <span className="expanded-value">{dealer.dealerMobile1}</span>
                </div>
                <div className="expanded-item">
                  <span className="expanded-label">Mobile 2:</span>
                  <span className="expanded-value">{dealer.dealerMobile2 || 'N/A'}</span>
                </div>
                <div className="expanded-item">
                  <span className="expanded-label">Access Level:</span>
                  <span className="expanded-value">{dealer.accesslevel || 'N/A'}</span>
                </div>
                <div className="expanded-item">
                  <span className="expanded-label">State:</span>
                  <span className="expanded-value">{dealer.state || 'N/A'}</span>
                </div>
              </div>
            </div>

            {dealer.offices && dealer.offices.length > 0 && (
              <div className="expanded-section">
                <h4>Officers</h4>
                <div className="offices-list">
                  {dealer.offices.map((office, idx) => (
                    <div key={idx} className="office-card">
                      <div className="office-header">
                        <strong>{office.officeName}</strong>
                      </div>
                      <div className="office-details">
                        <div><span>Email:</span> {office.officeemail}</div>
                        <div><span>Mobile:</span> {office.officeMobile1}</div>
                        {formatOfficeAddress(office) && <div><span>Address:</span> {formatOfficeAddress(office)}</div>}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </td>
      </tr>
    );
  };

  const DealerCard = ({ dealer }) => {
    const [showDetails, setShowDetails] = useState(false);

    return (
      <div className="dealer-card-wrapper">
        <div className="dealer-card">
          <div className="card-header">
            <div className="card-avatar-wrap">
              <div className="avatar" style={{ background: getAvatarColor(dealer.dealerName) }}>
                {getInitials(dealer.dealerName)}
              </div>
              <div className="card-identity">
                <span className="card-name">{dealer.dealerName}</span>
                <span className="card-email">{dealer.dealerEmail}</span>
              </div>
            </div>
            <div className="card-actions-header">
              <button className="card-action-icon edit" title="Edit" onClick={() => handleEditClick(dealer)}>
                <svg viewBox="0 0 18 18" fill="currentColor" width="14" height="14">
                  <path d="M12.5 2.5L15.5 5.5L6 15H3V12L12.5 2.5Z" stroke="currentColor" fill="none" strokeWidth="1.2" />
                  <path d="M10.5 4.5L13.5 7.5" stroke="currentColor" strokeWidth="1.2" />
                </svg>
              </button>
              <button className="card-action-icon delete" title="Delete" onClick={() => handleDeleteClick(dealer)}>
                <TrashIcon />
              </button>
              <button className="card-action-icon add-office" title="Add Officer" onClick={() => handleAddOfficeClick(dealer)}>
                <svg viewBox="0 0 18 18" fill="currentColor" width="14" height="14">
                  <path d="M9 3v12M3 9h12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                </svg>
              </button>
              <button className={`card-more-btn ${showDetails ? 'active' : ''}`} onClick={() => setShowDetails(!showDetails)}>
                {showDetails ? 'Less' : 'More'}
              </button>
            </div>
          </div>
          <div className="card-body">
            <div className="card-row">
              <span className="card-label">Phone</span>
              <span className="card-value phone">{dealer.dealerMobile1}</span>
            </div>
          </div>
          {showDetails && (
            <div className="card-expanded-details">
              <div className="expanded-section">
                <h4>Dealer Information</h4>
                <div className="expanded-grid">
                  <div className="expanded-item">
                    <span className="expanded-label">Dealer ID:</span>
                    <span className="expanded-value">{dealer.dealerId}</span>
                  </div>
                  <div className="expanded-item">
                    <span className="expanded-label">Mobile 2:</span>
                    <span className="expanded-value">{dealer.dealerMobile2 || 'N/A'}</span>
                  </div>
                  <div className="expanded-item">
                    <span className="expanded-label">Access Level:</span>
                    <span className="expanded-value">{dealer.accesslevel || 'N/A'}</span>
                  </div>
                  <div className="expanded-item">
                    <span className="expanded-label">State:</span>
                    <span className="expanded-value">{dealer.state || 'N/A'}</span>
                  </div>
                </div>
              </div>
              {dealer.offices && dealer.offices.length > 0 && (
                <div className="expanded-section">
                  <h4>Offices</h4>
                  {dealer.offices.map((office, idx) => (
                    <div key={idx} className="office-card-mini">
                      <strong>{office.officeName}</strong>
                      {formatOfficeAddress(office) && <div>{formatOfficeAddress(office)}</div>}
                      <div>Tel: {office.officeMobile1}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    );
  };

  if (loading && dealers.length === 0) {
    return (
      <div className="dealers-root">
        <div className="dealers-wrapper">
          <SkeletonTable title="Dealers" headings={["Dealer Name", "Email", "Phone", "Edit", "Delete", "Add Officer", "More"]} />
        </div>
      </div>
    );
  }

  return (
    <div className="dealers-root">
      <div className="dealers-wrapper">
        {/* Alert Banner */}
        {showAlert && (
          <div className={`alert-banner ${alertType}`}>
            <span>{alertMessage}</span>
            <button className="alert-close" onClick={() => setShowAlert(false)}>✕</button>
          </div>
        )}

        <div className="page-header">
          <div className="header-left">
            <h1 className="page-title">DEALERS</h1>
          </div>
          <div className="header-right">
            <div className="stat-pill">
              <span className="stat-dot" />
              <span className="stat-label">Records</span>
              <span className="stat-value">{filteredDealers.length}</span>
            </div>
            <button className="add-btn" onClick={handleAddDealerClick}>+ Add Dealer</button>
          </div>
        </div>

        <div className="toolbar">
          <div className="search-wrap">
            <svg className="search-icon" viewBox="0 0 20 20" fill="none">
              <circle cx="9" cy="9" r="6" stroke="currentColor" strokeWidth="1.8" />
              <path d="M13.5 13.5L17 17" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
            </svg>
            <input
              className="search-input"
              type="text"
              placeholder="Search dealers..."
              value={searchTerm}
              onChange={(e) => { 
                setSearchTerm(e.target.value);
                setCurrentPage(1);
              }}
            />
            {searchTerm && (
              <button className="search-clear" onClick={() => {
                setSearchTerm('');
                setCurrentPage(1);
              }}>✕</button>
            )}
          </div>
          <div className="rows-control">
            <span>Show</span>
            <select 
              value={rowsPerPage} 
              onChange={handleRowsPerPageChange}
            >
              {[5, 10, 25, 50, 100].map(n => <option key={n}>{n}</option>)}
            </select>
          </div>
        </div>

        {filteredDealers.length === 0 ? (
          <div className="empty-state">
            <div className="empty-icon">⊘</div>
            <p>No matching dealers found</p>
            <button className="reset-btn" onClick={() => {
              setSearchTerm('');
              setCurrentPage(1);
            }}>Clear search</button>
          </div>
        ) : (
          <>
            <div className="table-view">
              <div className="table-card">
                <div className="table-scroll">
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th>Dealer Name</th>
                        <th>Email</th>
                        <th>Phone</th>
                        <th className="text-center">Edit</th>
                        <th className="text-center">Delete</th>
                        <th className="text-center">Add Officer</th>
                        <th className="text-center">More</th>
                      </tr>
                    </thead>
                    <tbody>
                      {currentItems.map((dealer, idx) => {
                        const rowIndex = (currentPage - 1) * rowsPerPage + idx;
                        return (
                          <React.Fragment key={dealer.dealerId || idx}>
                            <tr className="data-row">
                              <td className="dealership-cell">
                                <div className="dealer-info">
                                  <div className="dealer-avatar" style={{ background: getAvatarColor(dealer.dealerName) }}>
                                    {getInitials(dealer.dealerName)}
                                  </div>
                                  <span className="dealer-name">{dealer.dealerName}</span>
                                </div>
                              </td>
                              <td className="email-cell">{dealer.dealerEmail}</td>
                              <td className="phone-cell">
                                <div className="phone-line">{dealer.dealerMobile1}</div>
                              </td>
                              <td className="edit-cell text-center">
                                <button className="action-icon edit-icon" title="Edit" onClick={() => handleEditClick(dealer)}>
                                  <svg viewBox="0 0 18 18" fill="currentColor" width="14" height="14">
                                    <path d="M12.5 2.5L15.5 5.5L6 15H3V12L12.5 2.5Z" stroke="currentColor" fill="none" strokeWidth="1.2" />
                                    <path d="M10.5 4.5L13.5 7.5" stroke="currentColor" strokeWidth="1.2" />
                                  </svg>
                                </button>
                              </td>
                              <td className="delete-cell text-center">
                                <button className="action-icon delete-icon" title="Delete" onClick={() => handleDeleteClick(dealer)}>
                                  <TrashIcon />
                                </button>
                              </td>
                              <td className="addoffice-cell text-center">
                                <button className="action-icon add-icon" title="Add Office" onClick={() => handleAddOfficeClick(dealer)}>
                                  <svg viewBox="0 0 18 18" fill="currentColor" width="14" height="14">
                                    <path d="M9 3v12M3 9h12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                                  </svg>
                                </button>
                              </td>
                              <td className="more-cell text-center">
                                <button
                                  className={`more-btn ${openDropdown === rowIndex ? 'active' : ''}`}
                                  onClick={() => toggleDropdown(rowIndex)}
                                >
                                  {openDropdown === rowIndex ? 'Less' : 'More'}
                                </button>
                              </td>
                            </tr>
                            <ExpandedRow dealer={dealer} isOpen={openDropdown === rowIndex} />
                          </React.Fragment>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                <div className="pagination-bar">
                  <div className="pagination-info">
                    Showing <strong>{startItem}–{endItem}</strong> of <strong>{totalItems}</strong>
                  </div>
                  <div className="pagination-controls">
                    <button 
                      className="pg-btn" 
                      onClick={() => handlePageChange(1)} 
                      disabled={currentPage === 1}
                    >
                      «
                    </button>
                    <button 
                      className="pg-btn" 
                      onClick={() => handlePageChange(currentPage - 1)} 
                      disabled={currentPage === 1}
                    >
                      ‹
                    </button>
                    {getPageNumbers().map((p, i) => {
                      if (p === '...') {
                        return <span key={`ellipsis-${i}`} className="pg-dots">…</span>;
                      }
                      return (
                        <button 
                          key={`page-${p}`} 
                          className={`pg-btn ${currentPage === p ? 'active' : ''}`} 
                          onClick={() => handlePageChange(p)}
                        >
                          {p}
                        </button>
                      );
                    })}
                    <button 
                      className="pg-btn" 
                      onClick={() => handlePageChange(currentPage + 1)} 
                      disabled={currentPage === totalPages}
                    >
                      ›
                    </button>
                    <button 
                      className="pg-btn" 
                      onClick={() => handlePageChange(totalPages)} 
                      disabled={currentPage === totalPages}
                    >
                      »
                    </button>
                  </div>
                  <div className="goto-wrap">
                    <span>Go to</span>
                    <form onSubmit={handleGoToPage}>
                      <input
                        type="number"
                        min="1"
                        max={totalPages}
                        value={goToPage}
                        onChange={e => setGoToPage(e.target.value)}
                        placeholder="—"
                      />
                      <button type="submit">↵</button>
                    </form>
                  </div>
                </div>
              </div>
            </div>

            <div className="card-view">
              <div className="cards-grid">
                {currentItems.map((dealer, idx) => (
                  <DealerCard key={dealer.dealerId || idx} dealer={dealer} />
                ))}
              </div>
              <div className="pagination-bar mobile-pagination">
                <div className="pagination-info">
                  {startItem}-{endItem} of {totalItems}
                </div>
                <div className="pagination-controls">
                  <button 
                    className="pg-btn" 
                    onClick={() => handlePageChange(currentPage - 1)} 
                    disabled={currentPage === 1}
                  >
                    ‹
                  </button>
                  <span className="page-current">{currentPage}/{totalPages}</span>
                  <button 
                    className="pg-btn" 
                    onClick={() => handlePageChange(currentPage + 1)} 
                    disabled={currentPage === totalPages}
                  >
                    ›
                  </button>
                </div>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      {isDeleteModalOpen && selectedDealer && (
        <div className="modal-overlay" onClick={() => setIsDeleteModalOpen(false)}>
          <div className="modal-container delete-modal" onClick={(e) => e.stopPropagation()}>
            <div className="delete-modal-content">
              <div className="delete-icon">🗑️</div>
              <h3>Are you sure? You want to delete Dealer - {selectedDealer.dealerName}</h3>
              <p>You can't revert your action and All offices related to this will be deleted</p>
              <div className="delete-modal-actions">
                <button className="delete-confirm-btn" onClick={handleConfirmDelete}>Yes Delete it!</button>
                <button className="delete-cancel-btn" onClick={() => setIsDeleteModalOpen(false)}>No, Keep it!</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Add Dealer Modal */}
      {isAddDealerModalOpen && (
        <div className="modal-overlay" onClick={() => setIsAddDealerModalOpen(false)}>
          <div className="modal-container add-dealer-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Add Dealer</h2>
              <button className="modal-close" onClick={() => setIsAddDealerModalOpen(false)}>✕</button>
            </div>
            <form onSubmit={handleAddDealerSubmit}>
              <div className="modal-body">
                <div className="form-group">
                  <label>Email <span className="required">*</span></label>
                  <input
                    type="email"
                    autoComplete="off"
                    value={dealerFormData.dealerEmail}
                    onChange={(e) => {
                      const dealerEmail = e.target.value;
                      setDealerFormData({ ...dealerFormData, dealerEmail, dealerName: dealerEmail.split('@')[0] });
                    }}
                    required
                  />
                  <small className="field-note">E-mail is required</small>
                </div>

                <div className="form-group">
                  <label>Dealer Name</label>
                  <input
                    type="text"
                    value={dealerFormData.dealerName}
                    onChange={(e) => setDealerFormData({ ...dealerFormData, dealerName: e.target.value })}
                    // placeholder="Generated from email"
                  />
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>Phone</label>
                    <div className="phone-input-wrapper">
                      <select className="country-code" value={dealerFormData.dealerPhoneCode1} onChange={(e) => setDealerFormData({ ...dealerFormData, dealerPhoneCode1: e.target.value })}>
                        <option value="+1">+1(US)</option>
                        <option value="+91">+91(IN)</option>
                        <option value="+44">+44(UK)</option>
                      </select>
                      <input
                        type="tel"
                        inputMode="numeric"
                        maxLength={10}
                        value={dealerFormData.dealerMobile1}
                        onChange={(e) => setDealerFormData({ ...dealerFormData, dealerMobile1: digitsOnly(e.target.value, 10) })}
                      />
                    </div>
                  </div>
                  <div className="form-group">
                    <label>Phone 2</label>
                    <div className="phone-input-wrapper">
                      <select className="country-code" value={dealerFormData.dealerPhoneCode2} onChange={(e) => setDealerFormData({ ...dealerFormData, dealerPhoneCode2: e.target.value })}>
                       <option value="+1">+1(US)</option>
                        <option value="+91">+91(IN)</option>
                        <option value="+44">+44(UK)</option>
                      </select>
                      <input
                        type="tel"
                        inputMode="numeric"
                        maxLength={10}
                        value={dealerFormData.dealerMobile2}
                        onChange={(e) => setDealerFormData({ ...dealerFormData, dealerMobile2: digitsOnly(e.target.value, 10) })}
                      />
                    </div>
                  </div>
                </div>

                <div className="form-group">
                  <label>Password <span className="required">*</span></label>
                  <div className="password-input-wrapper">
                  <input
                    type={showDealerPassword ? 'text' : 'password'}
                    autoComplete="new-password"
                    value={dealerFormData.dealerPassword}
                    onChange={(e) => setDealerFormData({ ...dealerFormData, dealerPassword: e.target.value })}
                    required
                  />
                  <button type="button" className="password-toggle" aria-label={showDealerPassword ? 'Hide password' : 'Show password'} onClick={() => setShowDealerPassword(value => !value)}>
                    {showDealerPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                  </button>
                  </div>
                  <small className="field-note">Password is required</small>
                </div>

                <div className="form-group">
                  <label>Address</label>
                  <input
                    type="text"
                    value={dealerFormData.address}
                    onChange={(e) => setDealerFormData({ ...dealerFormData, address: e.target.value })}
                  />
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>City</label>
                    <input
                      type="text"
                      value={dealerFormData.city}
                      onChange={(e) => setDealerFormData({ ...dealerFormData, city: cityOnly(e.target.value) })}
                    />
                  </div>
                  <div className="form-group">
                    <label>Select State</label>
                    <select
                      value={dealerFormData.state}
                      onChange={(e) => setDealerFormData({ ...dealerFormData, state: e.target.value })}
                    >
                      <option value="">Select State</option>
                      {usStates.map(state => (
                        <option key={state} value={state}>{state}</option>
                      ))}
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Zipcode</label>
                    <input
                      type="text"
                      value={dealerFormData.zipcode}
                      onChange={(e) => setDealerFormData({ ...dealerFormData, zipcode: digitsOnly(e.target.value, 6) })}
                    />
                  </div>
                </div>

                <div className="checkbox-group">
                  <label className="checkbox-label">
                    <input type="checkbox" checked={isOfficeSameAsDealer} onChange={(e) => {
                      const checked = e.target.checked;
                      setIsOfficeSameAsDealer(checked);
                      if (checked) setOfficeFormData(prev => ({ ...prev,
                        officeName: dealerFormData.dealerName, officeMobile1: dealerFormData.dealerMobile1,
                        officeMobile2: dealerFormData.dealerMobile2, officePhoneCode1: dealerFormData.dealerPhoneCode1,
                        officePhoneCode2: dealerFormData.dealerPhoneCode2, officeemail: dealerFormData.dealerEmail,
                        officeaddress: dealerFormData.address, city: dealerFormData.city,
                        state: dealerFormData.state, zipcode: dealerFormData.zipcode
                      }));
                    }} />
                    <span>Is officer details same as dealer?</span>
                  </label>
                </div>

                {isOfficeSameAsDealer && <div className="office-details-section">
                  <h3>Add Officer Details</h3>
                  <div className="form-row">
                    <div className="form-group"><label>Officer Name</label><input type="text" value={officeFormData.officeName} onChange={(e) => setOfficeFormData({ ...officeFormData, officeName: e.target.value })} /></div>
                    <div className="form-group"><label>Phone</label><div className="phone-input-wrapper"><select className="country-code" value={officeFormData.officePhoneCode1} onChange={(e) => setOfficeFormData({ ...officeFormData, officePhoneCode1: e.target.value })}><option value="+1">+1</option><option value="+91">+91</option><option value="+44">+44</option></select><input type="tel" inputMode="numeric" maxLength={10} value={officeFormData.officeMobile1} onChange={(e) => setOfficeFormData({ ...officeFormData, officeMobile1: digitsOnly(e.target.value, 10) })} /></div></div>
                  </div>
                  <div className="form-row">
                    <div className="form-group"><label>Phone 2</label><div className="phone-input-wrapper"><select className="country-code" value={officeFormData.officePhoneCode2} onChange={(e) => setOfficeFormData({ ...officeFormData, officePhoneCode2: e.target.value })}><option value="+1">+1</option><option value="+91">+91</option><option value="+44">+44</option></select><input type="tel" inputMode="numeric" maxLength={10} value={officeFormData.officeMobile2} onChange={(e) => setOfficeFormData({ ...officeFormData, officeMobile2: digitsOnly(e.target.value, 10) })} /></div></div>
                    <div className="form-group"><label>Email <span className="required">*</span></label><input type="email" autoComplete="off" value={officeFormData.officeemail} onChange={(e) => {
                      const officeemail = e.target.value;
                      setOfficeFormData({ ...officeFormData, officeemail, officeName: officeemail.split('@')[0] });
                    }} required /></div>
                  </div>
                  <div className="form-row">
                    <div className="form-group"><label>Password <span className="required">*</span></label><div className="password-input-wrapper"><input type={showOfficePassword ? 'text' : 'password'} autoComplete="new-password" value={officeFormData.officepassword} onChange={(e) => setOfficeFormData({ ...officeFormData, officepassword: e.target.value })} required /><button type="button" className="password-toggle" aria-label={showOfficePassword ? 'Hide password' : 'Show password'} onClick={() => setShowOfficePassword(value => !value)}>{showOfficePassword ? <EyeOff size={20} /> : <Eye size={20} />}</button></div></div>
                    <div className="form-group"><label>Address</label><input type="text" value={officeFormData.officeaddress} onChange={(e) => setOfficeFormData({ ...officeFormData, officeaddress: e.target.value })} /></div>
                  </div>
                  <div className="form-row">
                    <div className="form-group"><label>City</label><input type="text" value={officeFormData.city} onChange={(e) => setOfficeFormData({ ...officeFormData, city: cityOnly(e.target.value) })} /></div>
                    <div className="form-group"><label>State</label><select value={officeFormData.state} onChange={(e) => setOfficeFormData({ ...officeFormData, state: e.target.value })}><option value="">Select State</option>{usStates.map(state => <option key={state} value={state}>{state}</option>)}</select></div>
                    <div className="form-group"><label>Zipcode</label><input type="text" inputMode="numeric" maxLength={6} value={officeFormData.zipcode} onChange={(e) => setOfficeFormData({ ...officeFormData, zipcode: digitsOnly(e.target.value, 6) })} /></div>
                  </div>
                  <div className="form-row">
                    <div className="checkbox-group"><label className="checkbox-label"><input type="checkbox" checked={officeFormData.isbar === '1'} onChange={(e) => setOfficeFormData({ ...officeFormData, isbar: e.target.checked ? '1' : '0' })} /><span>isBAR License</span></label></div>
                    <div className="form-group"><label>BEARHFTI License</label><input type="text" value={officeFormData.officelicense} onChange={(e) => setOfficeFormData({ ...officeFormData, officelicense: e.target.value })} /></div>
                  </div>
                </div>}
              </div>
              <div className="modal-footer">
                <button type="button" className="cancel-btn" onClick={() => setIsAddDealerModalOpen(false)}>Cancel</button>
                <button type="submit" className="submit-btn">SAVE</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Dealer Modal */}
      {isEditModalOpen && selectedDealer && (
        <div className="modal-overlay" onClick={() => setIsEditModalOpen(false)}>
          <div className="modal-container edit-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Edit Dealer</h2>
              <button className="modal-close" onClick={() => setIsEditModalOpen(false)}>✕</button>
            </div>
            <form onSubmit={handleEditSubmit}>
              <div className="modal-body">
                <div className="form-group">
                  <label>Dealer Name <span className="required">*</span></label>
                  <input
                    type="text"
                    value={dealerFormData.dealerName}
                    onChange={(e) => setDealerFormData({ ...dealerFormData, dealerName: e.target.value })}
                    required
                  />
                </div>

                <div className="form-group">
                  <label>Email <span className="required">*</span></label>
                  <input
                    type="email"
                    value={dealerFormData.dealerEmail}
                    onChange={(e) => setDealerFormData({ ...dealerFormData, dealerEmail: e.target.value })}
                    required
                  />
                </div>

                <div className="form-group">
                  <label>Phone <span className="required">*</span></label>
                  <div className="phone-input-wrapper">
                    <select className="country-code" value={dealerFormData.dealerPhoneCode1} onChange={(e) => setDealerFormData({ ...dealerFormData, dealerPhoneCode1: e.target.value })}>
                      <option value="+1">+1</option>
                      <option value="+91">+91</option>
                      <option value="+44">+44</option>
                    </select>
                    <input
                      type="tel"
                      inputMode="numeric"
                      maxLength={10}
                      value={dealerFormData.dealerMobile1}
                      onKeyDown={preventNonNumericInput}
                      onChange={(e) => setDealerFormData({ ...dealerFormData, dealerMobile1: digitsOnly(e.target.value, 10) })}
                      required
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label>Phone 2</label>
                  <div className="phone-input-wrapper">
                    <select className="country-code" value={dealerFormData.dealerPhoneCode2} onChange={(e) => setDealerFormData({ ...dealerFormData, dealerPhoneCode2: e.target.value })}>
                      <option value="+1">+1</option>
                      <option value="+91">+91</option>
                      <option value="+44">+44</option>
                    </select>
                    <input
                      type="tel"
                      inputMode="numeric"
                      maxLength={10}
                      value={dealerFormData.dealerMobile2}
                      onKeyDown={preventNonNumericInput}
                      onChange={(e) => setDealerFormData({ ...dealerFormData, dealerMobile2: digitsOnly(e.target.value, 10) })}
                    />
                  </div>
                </div>

                {/* STATE */}
<div className="form-group">
  <label>State</label>

  <select
    value={dealerFormData.state}
    onChange={(e) =>
      setDealerFormData({
        ...dealerFormData,
        state: e.target.value
      })
    }
  >
    <option value="">Select State</option>

    {usStates.map((state) => (
      <option key={state} value={state}>
        {state}
      </option>
    ))}
  </select>
</div>

                <div className="form-group">
                  <label>Password</label>
                  <div className="password-input-wrapper">
                    <input
                      type={showDealerPassword ? 'text' : 'password'}
                      autoComplete="new-password"
                      value={dealerFormData.dealerPassword}
                      onChange={(e) => setDealerFormData({ ...dealerFormData, dealerPassword: e.target.value })}
                    />
                    <button type="button" className="password-toggle" aria-label={showDealerPassword ? 'Hide password' : 'Show password'} onClick={() => setShowDealerPassword(value => !value)}>
                      {showDealerPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                    </button>
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="cancel-btn" onClick={() => setIsEditModalOpen(false)}>Cancel</button>
                <button type="submit" className="submit-btn">UPDATE</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Add Office Modal */}
      {isAddOfficeModalOpen && selectedDealer && (
        <div className="modal-overlay" onClick={() => setIsAddOfficeModalOpen(false)}>
          <div className="modal-container add-office-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Add Officer for {selectedDealer.dealerName}</h2>
              <button className="modal-close" onClick={() => setIsAddOfficeModalOpen(false)}>✕</button>
            </div>
            <form onSubmit={handleAddOfficeSubmit} autoComplete="off">
              <div className="modal-body">
                <div className="form-group">
                  <label>Phone</label>
                  <div className="phone-input-wrapper">
                    <select className="country-code" value={officeFormData.officePhoneCode1} onChange={(e) => setOfficeFormData({ ...officeFormData, officePhoneCode1: e.target.value })}>
                      <option value="+1">+1</option>
                      <option value="+91">+91</option>
                      <option value="+44">+44</option>
                    </select>
                    <input
                      type="tel"
                      inputMode="numeric"
                      maxLength={10}
                      value={officeFormData.officeMobile1}
                      onChange={(e) => setOfficeFormData({ ...officeFormData, officeMobile1: digitsOnly(e.target.value, 10) })}
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label>Phone 2</label>
                  <div className="phone-input-wrapper">
                    <select className="country-code" value={officeFormData.officePhoneCode2} onChange={(e) => setOfficeFormData({ ...officeFormData, officePhoneCode2: e.target.value })}>
                      <option value="+1">+1</option>
                      <option value="+91">+91</option>
                      <option value="+44">+44</option>
                    </select>
                    <input
                      type="tel"
                      inputMode="numeric"
                      maxLength={10}
                      value={officeFormData.officeMobile2}
                      onChange={(e) => setOfficeFormData({ ...officeFormData, officeMobile2: digitsOnly(e.target.value, 10) })}
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label>Email <span className="required">*</span></label>
                  <input
                    type="email"
                    name="office-email"
                    autoComplete="off"
                    value={officeFormData.officeemail}
                    onChange={(e) => {
                      const officeemail = e.target.value;
                      setOfficeFormData({ ...officeFormData, officeemail, officeName: officeemail.split('@')[0] });
                    }}
                    required
                  />
                  <small className="field-note">E-mail is required</small>
                </div>

                <div className="form-group">
                  <label>Officer Name</label>
                  <input
                    type="text"
                    value={officeFormData.officeName}
                    onChange={(e) => setOfficeFormData({ ...officeFormData, officeName: e.target.value })}
                  />
                </div>

                <div className="form-group">
                  <label>Password <span className="required">*</span></label>
                  <div className="password-input-wrapper">
                    <input
                      type={showOfficePassword ? 'text' : 'password'}
                      name="office-password"
                      autoComplete="new-password"
                      value={officeFormData.officepassword}
                      onChange={(e) => setOfficeFormData({ ...officeFormData, officepassword: e.target.value })}
                      required
                    />
                    <button type="button" className="password-toggle" aria-label={showOfficePassword ? 'Hide password' : 'Show password'} onClick={() => setShowOfficePassword(value => !value)}>
                      {showOfficePassword ? <EyeOff size={20} /> : <Eye size={20} />}
                    </button>
                  </div>
                </div>

                <div className="form-group">
                  <label>Address</label>
                  <input
                    type="text"
                    value={officeFormData.officeaddress}
                    onChange={(e) => setOfficeFormData({ ...officeFormData, officeaddress: e.target.value })}
                  />
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>City</label>
                    <input
                      type="text"
                      value={officeFormData.city}
                      onChange={(e) => setOfficeFormData({ ...officeFormData, city: cityOnly(e.target.value) })}
                    />
                  </div>
                  <div className="form-group">
                    <label>Select State</label>
                    <select
                      value={officeFormData.state}
                      onChange={(e) => setOfficeFormData({ ...officeFormData, state: e.target.value })}
                    >
                      <option value="">Select State</option>
                      {usStates.map(state => (
                        <option key={state} value={state}>{state}</option>
                      ))}
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Zipcode</label>
                    <input
                      type="text"
                      value={officeFormData.zipcode}
                      inputMode="numeric"
                      maxLength={6}
                      onKeyDown={preventNonNumericInput}
                      onChange={(e) => setOfficeFormData({ ...officeFormData, zipcode: digitsOnly(e.target.value, 6) })}
                    />
                  </div>
                </div>

                <div className="checkbox-group">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={officeFormData.isbar === '1'}
                      onChange={(e) => setOfficeFormData({ ...officeFormData, isbar: e.target.checked ? '1' : '0' })}
                    />
                    <span>isBAR License</span>
                  </label>
                </div>

                <div className="form-group">
                  <label>BEARHFTI License</label>
                  <input
                    type="text"
                    value={officeFormData.officelicense}
                    onChange={(e) => setOfficeFormData({ ...officeFormData, officelicense: e.target.value })}
                  />
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="cancel-btn" onClick={() => setIsAddOfficeModalOpen(false)}>Cancel</button>
                <button type="submit" className="submit-btn">SAVE</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Dealers;
